<template>
  <Modal
    cancel-variant="primary"
    confirm-variant="primary-ghost"
    :no-confirm="isReplay"
    v-bind="{ isVisible }"
    :tracking="{
      action: 'checkout',
      topicKey: `player_${type}_limit_restriction`,
    }"
    @cancel="navigateToLocalized('/account/settings/limits')"
    @confirm="onConfirm"
  >
    <template #headline>
      {{ t(`${TRANSLATION_PREFIX}.${type}limit.title`) }}
    </template>

    <p>{{ limitMessage }}</p>

    <template #confirm>{{ t(`${TRANSLATION_PREFIX}.limit.confirm`) }}</template>
    <template #cancel>{{ t(`${TRANSLATION_PREFIX}.limit.cancel`) }}</template>
  </Modal>
</template>

<script lang="ts" setup>
import type { LimitPeriod, Money } from '#api/shared/@types/generated/backend/graphql-schema-types'

type CheckoutLimitModalEmit = {
  (e: 'confirm'): void
}

type CheckoutLimitModalProps = {
  availableAmount: Money
  grossTotal?: Money
  isVisible: boolean
  limit: Money
  period: LimitPeriod
} & (
  | {
      balance: Money
      type: 'deposit'
    }
  | { type: 'spending' }
)

const TRANSLATION_PREFIX = 'checkout.restrictions'

const { navigateToLocalized } = useRouterUtils()

const emit = defineEmits<CheckoutLimitModalEmit>()

const props = defineProps<CheckoutLimitModalProps>()

const { t } = useI18n()
const { navigateBack } = useRouterUtils()
const route = useRoute()
const routeBaseName = useRouteBaseName()

const isReplay = computed(() => String(route.query.replay) === 'true')

const limitMessage = computed(() => {
  const { availableAmount, grossTotal, limit, period, type } = props
  const variables = {
    availableAmount: formatMoney(availableAmount),
    limitAmount: formatMoney(limit),
    periodLimit: t(`checkout.restrictions.limit.${period}`),
    remainingPeriod: t(`checkout.restrictions.limit.remaining.${period}`),
    ...(type === 'deposit' && {
      balance: formatMoney(props.balance),
    }),
    ...(grossTotal && {
      grossTotal: formatMoney(grossTotal),
    }),
  }

  return t(`${TRANSLATION_PREFIX}.${type}limit.message`, variables)
})

const onConfirm = () => {
  const name = routeBaseName(route)
  const hasBackroute = typeof route.query.backroute === 'string'

  switch (name) {
    case 'keno-gameId':
    case 'scratchcard-slug':
      emit('confirm')
      break
    default:
      navigateBack({
        previousRoute: hasBackroute ? (route.query.backroute as string) : undefined,
      })
  }
}
</script>
